import "@/App.css";
import { Capacitor } from "@capacitor/core";
import { App as NativeApp } from "@capacitor/app";
import { useEffect, useState } from "react";
import { getLang } from "@/lib/i18n";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { DataProvider } from "@/context/DataContext";
import AuthPage from "@/pages/AuthPage";
import { Splash } from "@/components/mm/Brand";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import LegalPage from "@/pages/LegalPage";
import { api, errMsg } from "@/lib/api";
import { BrandMark } from "@/components/mm/Brand";
import { AppLockGate } from "@/components/mm/AppLock";


function AgeGate() {
  const { setUser, signOut, finishAccountDeletion } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [deletion, setDeletion] = useState({ confirmation: "", password: "", mfa_code: "" });
  const accept = async (event) => {
    event.preventDefault();setBusy(true);setError("");
    try { const { data } = await api.post("/auth/confirm-age", { age_18_confirmed: true });setUser(data); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const deleteAccount = async (event) => {
    event.preventDefault();setBusy(true);setError("");
    try { await api.delete("/auth/account", { data: deletion });await finishAccountDeletion(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return <main className="grid min-h-[100dvh] place-items-center bg-[#07101f] p-5 text-white" data-testid="age-gate">
    <div className="mm-card w-full max-w-lg space-y-5 text-center">
      <BrandMark className="mx-auto h-20 w-20" />
      <div><h1 className="text-2xl font-bold">BudgetPulse is for adults aged 18+</h1><p className="mt-2 text-sm text-[var(--mu)]">You must be 18 or older to create an account or use the service. We record your confirmation and policy version; we do not ask for your date of birth or identity document here.</p></div>
      <form onSubmit={accept} className="space-y-4"><label className="flex items-start gap-3 rounded-xl border border-[var(--bd)] p-4 text-left text-sm"><input className="mt-1" type="checkbox" checked={confirmed} onChange={(e)=>setConfirmed(e.target.checked)} required data-testid="age-gate-checkbox" /><span><b>I confirm that I am 18 years of age or older.</b><small className="mt-1 block text-[var(--mu)]">By continuing, you also acknowledge the current <Link className="text-[var(--ac)] underline" to="/terms">Terms</Link> and <Link className="text-[var(--ac)] underline" to="/privacy">Privacy Policy</Link>.</small></span></label><button className="mm-btn w-full justify-center" disabled={!confirmed || busy} data-testid="age-gate-submit">{busy ? "Saving…" : "Confirm and continue"}</button></form>
      {error && <p className="text-sm text-[var(--dg)]" role="alert">{error}</p>}
      <button className="text-sm text-[var(--mu)] underline" type="button" onClick={signOut}>Sign out</button>
      <details className="border-t border-[var(--bd)] pt-4 text-left"><summary className="cursor-pointer text-sm text-[var(--dg)]">Delete my account instead</summary><form className="mt-4 space-y-3" onSubmit={deleteAccount}><p className="text-xs text-[var(--mu)]">This permanently deletes active account data. Enter your password if the account has one, your MFA code if enabled, and the exact confirmation text.</p><input className="mm-input" type="password" placeholder="Password (if applicable)" value={deletion.password} onChange={(e)=>setDeletion({...deletion,password:e.target.value})} /><input className="mm-input" placeholder="Authenticator/recovery code (if enabled)" value={deletion.mfa_code} onChange={(e)=>setDeletion({...deletion,mfa_code:e.target.value})} /><input className="mm-input" placeholder="Type DELETE MY ACCOUNT" value={deletion.confirmation} onChange={(e)=>setDeletion({...deletion,confirmation:e.target.value})} required /><button className="mm-btn danger w-full justify-center" disabled={busy || deletion.confirmation!=="DELETE MY ACCOUNT"}>Permanently delete account</button></form></details>
    </div>
  </main>;
}


function Protected({ children }) {
  const { user, authError, retryAuth } = useAuth();
  if (user === null) return authError ? <AuthRetry message={authError} retry={retryAuth} /> : <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.age_18_confirmed) return <AgeGate />;
  return children;
}

function Public({ children }) {
  const { user, authError, retryAuth } = useAuth();
  if (user === null) return authError ? <AuthRetry message={authError} retry={retryAuth} /> : <Splash />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AuthRetry({ message, retry }) {
  const [busy, setBusy] = useState(false);
  return <main className="grid min-h-[100dvh] place-items-center p-5"><div className="mm-card max-w-md text-center"><BrandMark className="mx-auto h-16 w-16" /><p className="mt-4" role="alert">{message}</p><button className="mm-btn mt-4" disabled={busy} onClick={async () => { setBusy(true); try { await retry(); } finally { setBusy(false); } }}>{busy ? 'Checking…' : 'Retry session check'}</button></div></main>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Public><AuthPage /></Public>} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/app" element={<Protected><DataProvider><Dashboard /></DataProvider></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [, setLanguageVersion] = useState(0);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener;
    NativeApp.addListener("appUrlOpen", ({ url }) => {
      try {
        const target = new URL(url);
        if (target.protocol !== "budgetpulse:" || target.hostname !== "auth") return;
        const sid = new URLSearchParams(target.hash.slice(1)).get("session_id");
        if (!sid || !/^[A-Za-z0-9_-]{8,4096}$/.test(sid)) return;
        window.history.pushState(null, "", "/#session_id=" + encodeURIComponent(sid));
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch { /* Ignore unrelated or invalid deep links. */ }
    }).then((handle) => { listener = handle; });
    return () => { listener?.remove(); };
  }, []);
  useEffect(() => {
    document.documentElement.lang = getLang();
    const refresh = () => setLanguageVersion((n) => n + 1);
    window.addEventListener("bp-language-changed", refresh);
    return () => window.removeEventListener("bp-language-changed", refresh);
  }, []);
  return (
    <AppLockGate>
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton visibleToasts={2} offset={{ top:"max(12px, env(safe-area-inset-top))" }} toastOptions={{ style:{ background:"var(--card)", color:"var(--tx)", border:"1px solid var(--bd)", borderRadius:"16px" } }} />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
    </AppLockGate>
  );
}
